import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { authService } from '@/services/auth';
import toast from 'react-hot-toast';
import { cn } from '@/utils/cn';

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

export default function PasswordChangeForm() {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      authService.changePassword(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Password changed successfully');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setErrors({});
      } else {
        toast.error(response.error?.message ?? 'Failed to change password');
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(error.message ?? 'Failed to change password');
    },
  });

  const getPasswordStrength = (password: string): PasswordStrength => {
    if (!password) {
      return { score: 0, label: '', color: '' };
    }

    let score = 0;

    // Length
    if (password.length >= 8) { score++; }
    if (password.length >= 12) { score++; }

    // Contains lowercase
    if (/[a-z]/.test(password)) { score++; }

    // Contains uppercase
    if (/[A-Z]/.test(password)) { score++; }

    // Contains number
    if (/\d/.test(password)) { score++; }

    // Contains special char
    if (/[^a-zA-Z0-9]/.test(password)) { score++; }

    if (score <= 2) {
      return { score, label: 'Weak', color: 'text-danger-600 dark:text-danger-400' };
    } else if (score <= 4) {
      return { score, label: 'Medium', color: 'text-warning-600 dark:text-warning-400' };
    } else {
      return { score, label: 'Strong', color: 'text-success-600 dark:text-success-400' };
    }
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  const getPasswordRequirements = () => {
    const requirements = [
      { met: formData.newPassword.length >= 8, text: 'At least 8 characters' },
      { met: /[a-z]/.test(formData.newPassword), text: 'One lowercase letter' },
      { met: /[A-Z]/.test(formData.newPassword), text: 'One uppercase letter' },
      { met: /\d/.test(formData.newPassword), text: 'One number' },
      { met: /[^a-zA-Z0-9]/.test(formData.newPassword), text: 'One special character' },
    ];
    return requirements;
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (formData.newPassword === formData.currentPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) { return; }

    await changePasswordMutation.mutateAsync({
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword,
    });
  };

  const handleReset = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Current Password */}
      <div>
        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
          Current Password *
        </label>
        <div className="relative">
          <Input
            type={showPasswords.current ? 'text' : 'password'}
            value={formData.currentPassword}
            onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
            className={cn('pr-10', errors.currentPassword ? 'border-danger-500' : '')}
            disabled={changePasswordMutation.isPending}
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
          >
            {showPasswords.current ? (
              <EyeSlashIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        </div>
        {errors.currentPassword && (
          <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.currentPassword}</p>
        )}
      </div>

      {/* New Password */}
      <div>
        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
          New Password *
        </label>
        <div className="relative">
          <Input
            type={showPasswords.new ? 'text' : 'password'}
            value={formData.newPassword}
            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
            className={cn('pr-10', errors.newPassword ? 'border-danger-500' : '')}
            disabled={changePasswordMutation.isPending}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
          >
            {showPasswords.new ? (
              <EyeSlashIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        </div>
        {errors.newPassword && (
          <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.newPassword}</p>
        )}

        {/* Password Strength Indicator */}
        {formData.newPassword && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-secondary-600 dark:text-secondary-400">Password Strength:</span>
              <span className={cn('text-xs font-medium', passwordStrength.color)}>
                {passwordStrength.label}
              </span>
            </div>
            <div className="h-1 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all duration-300',
                  passwordStrength.score <= 2 ? 'bg-danger-500' :
                    passwordStrength.score <= 4 ? 'bg-warning-500' :
                      'bg-success-500'
                )}
                style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Password Requirements */}
        {formData.newPassword && (
          <div className="mt-3 space-y-1">
            {getPasswordRequirements().map((req, index) => (
              <div key={index} className="flex items-center space-x-2 text-xs">
                {req.met ? (
                  <CheckCircleIcon className="w-4 h-4 text-success-500" />
                ) : (
                  <XCircleIcon className="w-4 h-4 text-secondary-400" />
                )}
                <span className={cn(
                  req.met
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-secondary-600 dark:text-secondary-400'
                )}>
                  {req.text}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
          Confirm New Password *
        </label>
        <div className="relative">
          <Input
            type={showPasswords.confirm ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            className={cn('pr-10', errors.confirmPassword ? 'border-danger-500' : '')}
            disabled={changePasswordMutation.isPending}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
          >
            {showPasswords.confirm ? (
              <EyeSlashIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.confirmPassword}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
        <Button
          type="button"
          variant="outline"
          onClick={handleReset}
          disabled={changePasswordMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={changePasswordMutation.isPending}
        >
          {changePasswordMutation.isPending ? 'Changing Password...' : 'Change Password'}
        </Button>
      </div>
    </form>
  );
}
